import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, CATEGORIES, errMsg } from "@/lib/api";
import { Container, SectionLabel, CampaignCard, CardGridSkeleton, EmptyState } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import Seo from "@/components/Seo";
import { Search, SlidersHorizontal, Frown, Crown, BadgeCheck } from "lucide-react";

export default function Browse() {
  const [campaigns, setCampaigns] = useState(null);
  const [category, setCategory] = useState("All");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [proOnly, setProOnly] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [trust, setTrust] = useState(null);

  const load = () => {
    setCampaigns(null);
    api.get("/campaigns", { params: { status: "live", category, q: search || undefined, pro_only: proOnly || undefined, verified_only: verifiedOnly || undefined, limit: 60 } })
      .then((r) => setCampaigns(r.data))
      .catch(() => setCampaigns([]));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, search, proOnly, verifiedOnly]);
  useEffect(() => { api.get("/campaigns/trust-stats").then((r) => setTrust(r.data)).catch(() => {}); }, []);

  return (
    <div className="bg-white">
      <Seo title="Browse Fundraisers" description="Support a reviewed campaign on FightFund." />
      <section className="border-b border-ff-border bg-[#F3F4F6]">
        <Container className="py-12">
          <SectionLabel>Browse fundraisers</SectionLabel>
          <h1 className="mt-2 font-heading text-3xl font-extrabold tracking-tight text-ff-blue sm:text-4xl">
            Support a reviewed campaign
          </h1>
          {trust && trust.pct_funds_verified > 0 && (
            <div data-testid="browse-trust-stat" className="mt-3 inline-flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-[#ECFDF5] px-3 py-2 text-sm font-semibold text-ff-green">
              <span className="inline-flex items-center gap-1.5">
                <BadgeCheck className="h-4 w-4" />
                <span data-testid="trust-stat-pct"><span className="font-extrabold">{trust.pct_funds_verified}%</span> of funds here are raised by verified, reviewed organizers</span>
              </span>
              <span className="hidden text-ff-green/50 sm:inline">·</span>
              <span className="text-ff-green/90">{trust.verified_campaigns} verified · {trust.pro_campaigns} Pro</span>
            </div>
          )}
          <form onSubmit={(e) => { e.preventDefault(); setSearch(q); }} className="mt-6 flex max-w-xl gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ff-medium" />
              <Input data-testid="browse-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search campaigns…" className="pl-9" />
            </div>
            <Button type="submit" data-testid="browse-search-button" className="bg-ff-blue text-white hover:bg-ff-blue/90">
              Search
            </Button>
          </form>
        </Container>
      </section>

      <Container className="grid gap-8 py-10 lg:grid-cols-12">
        <aside className="lg:col-span-3">
          <div className="sticky top-24 rounded-xl border border-ff-border bg-white p-5">
            <div className="mb-3 flex items-center gap-2 text-sm font-bold text-ff-blue">
              <SlidersHorizontal className="h-4 w-4" /> Categories
            </div>
            <div className="flex flex-wrap gap-2 lg:flex-col lg:gap-1">
              {["All", ...CATEGORIES].map((cat) => (
                <button key={cat} data-testid={`filter-${cat.toLowerCase()}`} onClick={() => setCategory(cat)}
                  className={`rounded-lg px-3 py-2 text-left text-sm font-semibold transition-colors duration-200 ${category === cat ? "bg-ff-blue text-white" : "text-ff-dark hover:bg-[#F3F4F6]"}`}>
                  {cat}
                </button>
              ))}
            </div>
            <div className="mt-4 border-t border-ff-border pt-4">
              <button onClick={() => setProOnly((v) => !v)} data-testid="filter-pro-only"
                className={`flex w-full items-center justify-between gap-2 rounded-lg border-2 px-3 py-2 text-sm font-bold transition-colors duration-200 ${proOnly ? "border-[#FDE68A] bg-[#FFFBEB] text-[#92400E]" : "border-ff-border text-ff-dark hover:border-[#FDE68A]"}`}>
                <span className="flex items-center gap-1.5"><Crown className="h-4 w-4" /> Pro organizers</span>
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${proOnly ? "bg-[#92400E] text-white" : "bg-[#F3F4F6] text-ff-medium"}`}>{proOnly ? "ON" : "OFF"}</span>
              </button>
              <button onClick={() => setVerifiedOnly((v) => !v)} data-testid="filter-verified-only"
                className={`mt-2 flex w-full items-center justify-between gap-2 rounded-lg border-2 px-3 py-2 text-sm font-bold transition-colors duration-200 ${verifiedOnly ? "border-[#A7F3D0] bg-[#ECFDF5] text-ff-green" : "border-ff-border text-ff-dark hover:border-[#A7F3D0]"}`}>
                <span className="flex items-center gap-1.5"><BadgeCheck className="h-4 w-4" /> Verified organizers</span>
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${verifiedOnly ? "bg-ff-green text-white" : "bg-[#F3F4F6] text-ff-medium"}`}>{verifiedOnly ? "ON" : "OFF"}</span>
              </button>
              <Link to="/pro" data-testid="browse-pro-directory-link" className="mt-2 block text-center text-xs font-semibold text-ff-blue underline">
                Explore the Pro directory →
              </Link>
            </div>
          </div>
        </aside>

        <div className="lg:col-span-9">
          {campaigns === null ? (
            <CardGridSkeleton count={6} />
          ) : campaigns.length === 0 ? (
            <EmptyState icon={Frown} title="No campaigns found"
              message="Try a different category or search term."
              testid="browse-empty" />
          ) : (
            <>
              <div className="mb-5 text-sm text-ff-medium">{campaigns.length} campaign{campaigns.length !== 1 ? "s" : ""}</div>
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 xl:grid-cols-3" data-testid="browse-grid">
                {campaigns.map((c) => <CampaignCard key={c.id} c={c} />)}
              </div>
            </>
          )}
        </div>
      </Container>
    </div>
  );
}
